import React, { useState } from 'react';

interface FormField {
    name: string;
    type: 'text' | 'email' | 'tel' | 'select';
    placeholder: string;
    required?: boolean;
    options?: { value: string; label: string }[];
}

interface PPEFormProps {
    title: string;
    description: string;
    phoneNumber: string;
    fields: FormField[];
    formName: string;
    submitButtonText?: string;
    className?: string;
}

const PPEForm: React.FC<PPEFormProps> = ({
    title,
    description,
    phoneNumber,
    fields,
    formName,
    submitButtonText = 'Submit',
    className = ''
}) => {
    const [formData, setFormData] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Format the form data according to the GraphQL mutation
    const formatFormData = (data: Record<string, string>) => {
        // Base form data
        const formattedData: Record<string, any> = {
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            company: data.company || '',
            quantity: data.quantity || '',
            form_name: formName
        };

        // Add form-specific fields
        Object.keys(data).forEach(key => {
            if (!['name', 'email', 'phone', 'company', 'quantity'].includes(key)) {
                formattedData[key] = data[key];
            }
        });

        return formattedData;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage(null);

        try {
            const formattedData = formatFormData(formData);
            
            // Create GraphQL mutation with direct input fields
            const mutation = `
                mutation { 
                    submitFormPPE(
                        input: {
                            name: ${JSON.stringify(formattedData.name || '')}
                            email: ${JSON.stringify(formattedData.email || '')}
                            phone: ${JSON.stringify(formattedData.phone || '')}
                            company: ${JSON.stringify(formattedData.company || '')}
                            quantity: ${JSON.stringify(formattedData.quantity || '')}
                            form_name: ${JSON.stringify(formattedData.form_name || '')}
                            ${Object.entries(formattedData)
                                .filter(([key]) => !['name', 'email', 'phone', 'company', 'quantity', 'form_name'].includes(key))
                                .map(([key, value]) => `${key}: ${JSON.stringify(value || '')}`)
                                .join('\n                            ')}
                        }
                    ) { 
                        success_message
                    }
                }
            `;

            const response = await fetch('https://live-magento.ipromo.com/graphql', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: mutation,
                    variables: {},
                }),
            });

            const result = await response.json();

            if (response.ok && !result.errors) {
                setSubmitMessage({ 
                    type: 'success', 
                    text: 'Thank you for your submission! We will get back to you shortly.' 
                });
                
                // Reset form data
                const resetData: Record<string, string> = {};
                fields.forEach(field => {
                    resetData[field.name] = '';
                });
                setFormData(resetData);
            } else {
                console.error('GraphQL errors:', result.errors);
                throw new Error(result.errors?.[0]?.message || 'Failed to submit form');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitMessage({ 
                type: 'error', 
                text: error instanceof Error ? error.message : 'Failed to submit form. Please try again.' 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderField = (field: FormField) => {
        if (field.type === 'select') {
            return (
                <select
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleInputChange}
                    className="ppe-form-select bg-white border border-gray-300 text-sm px-3 py-2 w-full rounded-md shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required={field.required}
                    style={{ color: formData[field.name] ? 'rgb(48, 48, 48)' : 'rgb(117, 117, 117)' }}
                >
                    <option value="" style={{ color: 'rgb(117, 117, 117)' }}>{field.placeholder}</option>
                    {field.options?.map((option) => (
                        <option key={option.value} value={option.value} style={{ color: 'rgb(48, 48, 48)' }}>
                            {option.label}
                        </option>
                    ))}
                </select>
            );
        }

        return (
            <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleInputChange}
                placeholder={field.placeholder}
                required={field.required}
                className="ppe-form-input bg-white border border-gray-300 text-sm h-11 px-3 py-2 w-full rounded-md text-gray-500 shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
        );
    };

    // Distribute fields into rows of max 3 items each
    const distributeFields = () => {
        const rows = [];
        const fieldsPerRow = 3;
        
        for (let i = 0; i < fields.length; i += fieldsPerRow) {
            const rowFields = fields.slice(i, i + fieldsPerRow);
            rows.push(rowFields);
        }
        
        return rows;
    };

    const fieldRows = distributeFields();
    const lastRow = fieldRows[fieldRows.length - 1];
    const canFitButtonInLastRow = lastRow && lastRow.length < 3;

    return (
        <div className={`w-full py-8 bg-white ${className}`}>
            <div className="ppe-form-container">
                <div className="p-6 ml-0 md:ml-[3%] pl-5 max-w-[75rem]">
                    <h2 className="ppe-form-heading">{title}</h2>
                    <p className="ppe-form-description">
                        {description} <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} className="text-red-600 underline">{phoneNumber}</a> to get started.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {fieldRows.map((rowFields, rowIndex) => {
                            const isLastRow = rowIndex === fieldRows.length - 1;
                            const showButtonInThisRow = isLastRow && canFitButtonInLastRow;
                            
                            return (
                                <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {rowFields.map((field) => (
                                        <div key={field.name}>
                                            {renderField(field)}
                                        </div>
                                    ))}
                                    
                                    {showButtonInThisRow && (
                                        <div className="flex items-end">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className={`ppe-form-submit px-6 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 h-11 ${
                                                    isSubmitting ? 'min-w-[150px]' : 'w-[117.656px]'
                                                } whitespace-nowrap`}
                                            >
                                                {isSubmitting ? 'Submitting...' : submitButtonText}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {!canFitButtonInLastRow && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`ppe-form-submit px-6 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 h-11 ${
                                        isSubmitting ? 'min-w-[150px]' : 'w-[117.656px]'
                                    } whitespace-nowrap`}
                                >
                                    {isSubmitting ? 'Submitting...' : submitButtonText}
                                </button>
                            </div>
                        )}

                        {submitMessage && (
                            <div className={`p-3 rounded-md ${
                                submitMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                                {submitMessage.text}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PPEForm; 